import classNames from 'classnames';
import debounce from 'lodash/debounce';
import isFunction from 'lodash/isFunction';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { memo, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { BANNER_TYPES } from '@sravni/react-advertisement';
import { AuthPopup } from '@sravni/react-ugc/lib/AuthPopup';
import { useBoolean, useIsMobile } from '@sravni/react-utils';

import { subscribeArticleButtonsOnGaEvents } from '../../../../helpers/analytics';
import { getMaterialLink, getMaterialProduct } from '../../../../helpers/news';
import {
  clearTextFromHTMLTagsAndBreak,
  getCurrentMaterialAliasForPathName,
  makeArticleMetadata,
} from '../../../../helpers/seo';
import { useAdMaterialTags, useBanners } from '../../../../hooks/advertising';
import { useTotalComments } from '../../../../hooks/comments';
import { useGenerateTabsForSpecialOffers } from '../../../../hooks/generateTabsForSpecialOffers';
import { useArticlesWithCommentsUserWantsToSee, useDictionaries, useRubricsHasSubDomain } from '../../../../hooks/news';
import { useIsUserLogged } from '../../../../hooks/users';
import { useSurveyWidget } from '../../../../hooks/useSurveyWidget';
import { IMaterial } from '../../../../interfaces/news';
import { fetchCommentsTotal } from '../../../../reducers/comments';
import { fetchArticleLikes } from '../../../../reducers/likes';
import { setSpecialOffersPopupTabs } from '../../../../reducers/specialOffers';
import { useWhitelabel } from '../../../../reducers/whitelabel';
import { getBanners } from '../../../../services/advertising';
import { signInUser, verifyUser } from '../../../../services/auth/user';
import commonStyles from '../../../../styles/common.module.scss';
import { isServer } from '../../../../utils/isServer';
import { LazyLoad } from '../../../../utils/lazyLoad';
import queue from '../../../../utils/queue';
import { BorderedBanner } from '../../../BorderedBanner';
import { RightColumnContent } from '../../../RightColumn';
import { SpecialOffersBlock } from '../../../SpecialOffersBlock';
import { injectHeader } from '../../helpers/injectHeader';
import { ArticleContent } from '../ArticleContent';
import { HideContainer } from '../HideContainer';

import styles from './article.module.scss';

const CommentsSection = dynamic(() => import('../../../CommentsSection'));

export interface IArticleProps {
  material: IMaterial;
  observer: IntersectionObserver;
  index: number;
  isShowArticle: boolean;
  isUserReadingThisArticle: boolean;
  titleTag?: React.ComponentProps<typeof ArticleContent>['titleTag'];
  canSpecialOffersBeLoaded: boolean;
  commentsObserver: IntersectionObserver;
}

const sleep = async (ms: number) => {
  return await new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

const executor = async (elem: MutableRefObject<HTMLElement>, callback: (el: HTMLElement) => void) => {
  let limiter = 10;
  do {
    if (elem.current) {
      callback(elem.current);
      break;
    }

    await sleep(100);
  } while (--limiter);
};

//TODO разнести компонент артикл на несколько компонентов
export const Article = memo<IArticleProps>(
  ({
    material,
    observer,
    index,
    isShowArticle,
    isUserReadingThisArticle,
    titleTag,
    canSpecialOffersBeLoaded,
    commentsObserver,
  }) => {
    const { query } = useRouter();
    const dispatch = useDispatch();
    const isMobile = useIsMobile();
    const articleRef = useRef<HTMLDivElement>();
    const triggerRef = useRef<HTMLDivElement>();
    const titleRef = useRef<HTMLDivElement>();
    const articleInnerRef = useRef<HTMLDivElement>();
    const commentsRef = useRef<HTMLDivElement>();
    const commentTextareaRef = useRef<HTMLTextAreaElement>();
    const isWhitelabel = useWhitelabel();
    const isUserLogged = useIsUserLogged();
    const isUserWantToSeeComments = useArticlesWithCommentsUserWantsToSee().includes(material.id);
    const rubricList = useRubricsHasSubDomain();

    const isMaterialNoIndexHasSubDomain = rubricList.includes(material.rubric);

    const bannersFromStore = useBanners(material.alias);
    const [banners, setBanners] = useState(bannersFromStore);
    const [articleHeight, setArticleHeight] = useState(0);
    const commentsCount = useTotalComments(material.id);
    const adTags = useAdMaterialTags();

    const { rubrics } = useDictionaries();
    const rubric = rubrics?.find((el) => el.value === material.rubric);
    const materialLink = getMaterialLink(material, {
      ...(material.rubric && { rubricAlias: material.rubric }),
    });

    const [isLoadingArticleBanners, setLoadingBanners] = useBoolean(!material.hideAds);
    const [showAuthPopup, setShowAuthPopup] = useBoolean(false);

    const tabsForSpecialOffers = useGenerateTabsForSpecialOffers(material);

    useEffect(() => {
      if (isUserReadingThisArticle) {
        dispatch(setSpecialOffersPopupTabs(tabsForSpecialOffers));
      }
    }, [dispatch, isUserReadingThisArticle, material?.productTags, tabsForSpecialOffers]);

    useEffect(() => {
      dispatch(fetchArticleLikes(material.id));
    }, [dispatch, material.id]);

    const articleMetadata = makeArticleMetadata(material, rubric, materialLink);

    const hideMobileBannerStyles =
      material.hideAds && isUserReadingThisArticle ? (
        <style>
          {`
            [data-js=mobileBottomBanner] {
              display: none;
            }
          `}
        </style>
      ) : null;

    const loadArticleBanners = async () => {
      const product = getMaterialProduct({ productTags: material.productTags, adTags });

      try {
        setLoadingBanners.on();
        //TODO: протипизировать данные которые приходят из getBanners
        const data = await getBanners({
          params: {
            BannerTypes: [BANNER_TYPES.SIDE, BANNER_TYPES.TGBM_7],
            Category: 'none',
            Product: product,
            Page: 'news',
          },
          source: material,
          adTags,
        });

        setBanners(data);
      } catch (e) {
      } finally {
        setLoadingBanners.off();
      }
    };

    useSurveyWidget(() => {
      if (articleInnerRef.current?.clientHeight) {
        setArticleHeight(articleInnerRef.current?.clientHeight);
      }
    });

    useEffect(() => {
      /** useEffect в котором инициализируются все обсерверы на ресайз стаьи и lazy-load картинок
       * нужно переподписываться каждый раз когда статья появляется, меняется props  isShowArticle*/
      let lazyLoad: LazyLoad;
      let resizeObserver: ResizeObserver;
      const articleBlock = articleInnerRef.current;

      if (isShowArticle) {
        const init = () => {
          queue.push(() => {
            if (articleHeight < articleBlock?.clientHeight) {
              setArticleHeight(articleBlock?.clientHeight);
            }
          });

          resizeObserver = new ResizeObserver(
            debounce((entries: ResizeObserverEntry[]) => {
              entries.forEach((entry) => {
                const { clientHeight } = entry.target;

                if (clientHeight && clientHeight !== 0 && articleHeight < clientHeight) {
                  setArticleHeight(clientHeight);
                }
              });
            }, 300),
          );

          resizeObserver.observe(articleBlock);
        };

        executor(articleRef, () => (lazyLoad = new LazyLoad(articleRef.current)));

        executor(articleInnerRef, init);
      }

      const removeSubscribe = subscribeArticleButtonsOnGaEvents(articleBlock);

      return () => {
        lazyLoad?.removeObserver();
        // resizeObserver?.disconnect();
        resizeObserver?.unobserve(articleBlock);
        if (isFunction(removeSubscribe)) {
          removeSubscribe();
        }
      };
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [isShowArticle]);

    useEffect(() => {
      if (!isWhitelabel) {
        !banners && queue.push(loadArticleBanners);
      } else {
        setLoadingBanners.off();
      }

      const trigger = triggerRef.current;
      const title = titleRef.current;
      const commentsElem = commentsRef.current;

      return () => {
        trigger && observer.unobserve(trigger);
        title && observer?.unobserve(title);
        commentsElem && commentsObserver.unobserve(commentsElem);
      };

      /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    useEffect(() => {
      if (isShowArticle) {
        executor(triggerRef, observer.observe.bind(observer));
        executor(titleRef, observer.observe.bind(observer));
        executor(commentsRef, commentsObserver.observe.bind(commentsObserver));
      } else {
        [triggerRef.current, titleRef.current].forEach((el) => {
          if (el) {
            observer.unobserve(el);
          }
        });
        commentsRef.current && commentsObserver?.unobserve(commentsRef.current);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShowArticle]);

    useEffect(() => {
      dispatch(fetchCommentsTotal(material.id));
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [material.id]);

    useEffect(() => {
      if (query.scrollToComments === material.id.toString() && commentsRef) {
        const topCoordinate = commentsRef.current.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ behavior: 'smooth', top: topCoordinate });
      }
    }, [material.id, query.scrollToComments]);

    const [iframeHeights, setIframeHeights] = useState({
      height: '275.5px',
      minHeight: '275.5px',
      maxHeight: '275.5px',
    });

    const onSOHeightChange = useCallback((height: number) => {
      const newHeight = `${height}px`;

      setIframeHeights({
        height: newHeight,
        minHeight: newHeight,
        maxHeight: newHeight,
      });
      setArticleHeight(articleInnerRef.current?.clientHeight);
    }, []);

    const specialOffers = !material.externalLink && !!tabsForSpecialOffers.length && (
      <div className={classNames(styles['spec-wrapper'], isMobile ? 'h-mt-20' : 'h-mt-32')} style={iframeHeights}>
        {canSpecialOffersBeLoaded && (
          <SpecialOffersBlock tabs={tabsForSpecialOffers} onHeightChange={onSOHeightChange} />
        )}
      </div>
    );

    const currentMaterialAliasIsPathName = !isServer && getCurrentMaterialAliasForPathName(document.location.pathname);

    return (
      <>
        {hideMobileBannerStyles}
        <Head>
          {injectHeader(material.htmlHead)}
          {!isServer && material.alias === currentMaterialAliasIsPathName && (
            <>
              <title key="title$">{material.seoTitle || material.title}</title>
              <meta
                key="description"
                name="description"
                content={material.seoDescription || clearTextFromHTMLTagsAndBreak(material.lead)}
              />
              <meta
                key="robots"
                name="robots"
                content={
                  material.noIndex || isMaterialNoIndexHasSubDomain
                    ? 'noindex, nofollow'
                    : 'index, follow, max-image-preview:large'
                }
              />
            </>
          )}
        </Head>
        {isServer && articleMetadata}
        <div ref={articleRef} className={styles.columnsWrapper} style={{ minHeight: articleHeight }}>
          <div
            ref={triggerRef}
            id={material.alias}
            className={styles.trigger}
            style={{ top: articleHeight / 2 }}
            data-trigger="loading"
            data-alias={material.alias}
          />
          <HideContainer
            hide={!isShowArticle && !!material.externalLink}
            remove={!isShowArticle && !material.externalLink}
          >
            <>
              <div ref={articleInnerRef} className={classNames(styles.articleColumn)}>
                <ArticleContent
                  material={material}
                  index={index}
                  commentsCount={commentsCount}
                  commentsRef={commentsRef}
                  banners={banners}
                  isLoadingBanners={isLoadingArticleBanners}
                  handleShowAuthPopup={setShowAuthPopup.on}
                  isUserLogged={isUserLogged}
                  titleTag={titleTag}
                  titleRef={titleRef}
                />
                <div ref={commentsRef} data-alias={material.alias}>
                  {(isUserWantToSeeComments || commentsCount > 0) && (
                    <CommentsSection
                      articleId={material.id}
                      canCreateNewComments={material.canCreateNewComments}
                      handleShowAuthPopup={setShowAuthPopup.on}
                      commentTextareaRef={commentTextareaRef}
                    />
                  )}
                </div>
                {!isWhitelabel && specialOffers}
                {!isWhitelabel && isMobile && (isLoadingArticleBanners || banners?.[BANNER_TYPES.SIDE]) && (
                  <div className={classNames(styles.sideBannerAtMobileBottom, 'h-mt-32', 'h-mb-32')}>
                    <BorderedBanner banner={banners?.[BANNER_TYPES.SIDE]} type={BANNER_TYPES.SIDE} lazy={true} />
                  </div>
                )}
              </div>
              <div className={commonStyles.rightColumn}>
                {!isWhitelabel && (
                  <RightColumnContent
                    readOnBlockMaterials={material.relationTo}
                    banners={banners}
                    isLoadingBanners={isLoadingArticleBanners}
                    isAdsHidden={material.hideAds}
                  />
                )}
              </div>
            </>
          </HideContainer>
          <AuthPopup
            showPopup={showAuthPopup}
            handleClose={setShowAuthPopup.off}
            signInUser={signInUser}
            verifyUser={verifyUser}
          />
        </div>
      </>
    );
  },
);
